import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('wyse_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e?.response?.status === 401) {
      // Clear stale auth so the UI updates to "Sign in"
      if (localStorage.getItem('wyse_token')) {
        localStorage.removeItem('wyse_token');
        localStorage.removeItem('wyse_user');
      }
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/login?next=' + encodeURIComponent(window.location.pathname);
      }
    }
    return Promise.reject(e);
  }
);

export default client;

export const api = {
  // public
  listTypes: () => client.get('/booking/appointment-types').then((r) => r.data),
  listAdvisors: () => client.get('/booking/advisors').then((r) => r.data),
  availability: (typeId, date, advisorId) =>
    client.get('/booking/availability', { params: { appointment_type_id: typeId, date, advisor_id: advisorId } }).then((r) => r.data),
  availabilityRange: (typeId, start, days = 30, advisorId) =>
    client.get('/booking/availability-range', { params: { appointment_type_id: typeId, start, days, advisor_id: advisorId } }).then((r) => r.data),
  createBooking: (payload, turnstile_token = '') => client.post('/booking/appointments', payload, { headers: turnstile_token ? { 'X-Turnstile-Token': turnstile_token } : {} }).then((r) => r.data),
  getBooking: (token) => client.get(`/booking/appointment/${token}`).then((r) => r.data),
  reschedule: (token, payload) => client.put(`/booking/appointment/${token}/reschedule`, payload).then((r) => r.data),
  cancel: (token, payload) => client.put(`/booking/appointment/${token}/cancel`, payload).then((r) => r.data),

  // auth
  login: (email, password, turnstile_token = '') => client.post('/auth/login', { email, password }, { headers: turnstile_token ? { 'X-Turnstile-Token': turnstile_token } : {} }).then((r) => r.data),
  signup: (payload, turnstile_token = '') => client.post('/auth/signup', payload, { headers: turnstile_token ? { 'X-Turnstile-Token': turnstile_token } : {} }).then((r) => r.data),
  me: () => client.get('/auth/me').then((r) => r.data),

  // member
  myProfile: () => client.get('/me/profile').then((r) => r.data),
  myAppointments: () => client.get('/me/appointments').then((r) => r.data),
  updateProfile: (payload) => client.put('/me/profile', payload).then((r) => r.data),

  // admin
  dashboard: () => client.get('/admin/dashboard').then((r) => r.data),
  adminAppointments: (params) => client.get('/admin/appointments', { params }).then((r) => r.data),
  calendar: (params) => client.get('/admin/calendar', { params }).then((r) => r.data),
  getAppointment: (id) => client.get(`/admin/appointments/${id}`).then((r) => r.data),
  updateStatus: (id, payload) => client.put(`/admin/appointments/${id}/status`, payload).then((r) => r.data),
  addNote: (id, body) => client.post(`/admin/appointments/${id}/notes`, { body }).then((r) => r.data),
  exportCsvUrl: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return `${API}/admin/export/appointments.csv?${q}`;
  },
  adminClients: (search) => client.get('/admin/clients', { params: { search } }).then((r) => r.data),
  adminClient: (id) => client.get(`/admin/clients/${id}`).then((r) => r.data),
  adminTypes: () => client.get('/admin/appointment-types').then((r) => r.data),
  createType: (payload) => client.post('/admin/appointment-types', payload).then((r) => r.data),
  updateType: (id, payload) => client.put(`/admin/appointment-types/${id}`, payload).then((r) => r.data),
  deleteType: (id) => client.delete(`/admin/appointment-types/${id}`).then((r) => r.data),
  adminAdvisors: () => client.get('/admin/advisors').then((r) => r.data),
  createAdvisor: (payload) => client.post('/admin/advisors', payload).then((r) => r.data),
  updateAdvisor: (id, payload) => client.put(`/admin/advisors/${id}`, payload).then((r) => r.data),
  getAvailability: (advisorId) => client.get(`/admin/availability/${advisorId}`).then((r) => r.data),
  setAvailability: (advisorId, rules) => client.put(`/admin/availability/${advisorId}`, rules).then((r) => r.data),
  listBlocked: (advisorId) => client.get('/admin/blocked-times', { params: { advisor_id: advisorId } }).then((r) => r.data),
  createBlocked: (payload) => client.post('/admin/blocked-times', payload).then((r) => r.data),
  deleteBlocked: (id) => client.delete(`/admin/blocked-times/${id}`).then((r) => r.data),
  getSettings: () => client.get('/admin/settings').then((r) => r.data),
  updateSettings: (payload) => client.put('/admin/settings', payload).then((r) => r.data),
  listNotifications: () => client.get('/admin/notifications').then((r) => r.data),
  adminCreateAppointment: (payload) => client.post('/admin/appointments', payload).then((r) => r.data),

  setMyLanguage: (preferred_lang) => client.put('/me/language', { preferred_lang }).then((r) => r.data),

  // --- Documents (shared) ---
  documentCategories: () => client.get('/document-categories').then((r) => r.data),

  // --- Documents (member) ---
  myDocuments: (params = {}) => client.get('/me/documents', { params }).then((r) => r.data),
  myDocument: (id) => client.get(`/me/documents/${id}`).then((r) => r.data),
  myDocumentDownloadLink: (id) => client.get(`/me/documents/${id}/download-link`).then((r) => r.data),
  myDocumentNotes: (id) => client.get(`/me/documents/${id}/notes`).then((r) => r.data),
  myDocumentNoteCreate: (id, body, parent_id = null) =>
    client.post(`/me/documents/${id}/notes`, { body, internal_only: false, parent_id }).then((r) => r.data),
  myDocumentDelete: (id) => client.delete(`/me/documents/${id}`).then((r) => r.data),
  myDocumentVersionUpload: (id, formData) =>
    client.post(`/me/documents/${id}/versions`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
  myDocumentUpload: (formData, onProgress) =>
    client.post('/me/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    }).then((r) => r.data),
  myDocumentRequests: () => client.get('/me/document-requests').then((r) => r.data),
  mySignatureRequests: () => client.get('/me/signature-requests').then((r) => r.data),
  mySignatureUploadSigned: (id, formData) =>
    client.post(`/me/signature-requests/${id}/upload-signed`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
  myTaxFile: (tax_year) =>
    client.get('/me/tax-file', { params: tax_year ? { tax_year } : {} }).then((r) => r.data),

  // --- Documents (specialist) ---
  specialistDashboard: () => client.get('/specialist/dashboard').then((r) => r.data),
  specialistMembers: () => client.get('/specialist/members').then((r) => r.data),
  specialistMemberWorkspace: (memberId, tax_year) =>
    client.get(`/specialist/members/${memberId}/workspace`, { params: tax_year ? { tax_year } : {} }).then((r) => r.data),
  specialistDocument: (id) => client.get(`/specialist/documents/${id}`).then((r) => r.data),
  specialistDocumentDownloadLink: (id) => client.get(`/specialist/documents/${id}/download-link`).then((r) => r.data),
  specialistDocStatus: (id, payload) => client.put(`/specialist/documents/${id}/status`, payload).then((r) => r.data),
  specialistDocMetadata: (id, payload) => client.put(`/specialist/documents/${id}/metadata`, payload).then((r) => r.data),
  specialistAddNote: (id, body, internal_only, parent_id = null) =>
    client.post(`/specialist/documents/${id}/notes`, { body, internal_only, parent_id }).then((r) => r.data),
  specialistRequestDoc: (payload) => client.post('/specialist/document-requests', payload).then((r) => r.data),
  specialistCloseRequest: (id) => client.put(`/specialist/document-requests/${id}/close`).then((r) => r.data),
  specialistCreateSignature: (payload) => client.post('/specialist/signature-requests', payload).then((r) => r.data),
  specialistSignatureStatus: (id, status) => client.put(`/specialist/signature-requests/${id}/status`, { status }).then((r) => r.data),
  specialistUploadForMember: (memberId, formData) =>
    client.post(`/specialist/members/${memberId}/documents`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
  specialistTaxFileStatus: (memberId, taxYear, payload) =>
    client.put(`/specialist/members/${memberId}/tax-file/${taxYear}`, payload).then((r) => r.data),

  // --- Documents (admin) ---
  adminCategories: () => client.get('/admin/document-categories').then((r) => r.data),
  adminCreateCategory: (payload) => client.post('/admin/document-categories', payload).then((r) => r.data),
  adminUpdateCategory: (id, payload) => client.put(`/admin/document-categories/${id}`, payload).then((r) => r.data),
  adminDeleteCategory: (id) => client.delete(`/admin/document-categories/${id}`).then((r) => r.data),
  adminAuditLogs: (params) => client.get('/admin/audit-logs', { params }).then((r) => r.data),
  adminAssignments: () => client.get('/admin/assignments').then((r) => r.data),
  adminCreateAssignment: (payload) => client.post('/admin/assignments', payload).then((r) => r.data),
  adminDeleteAssignment: (id) => client.delete(`/admin/assignments/${id}`).then((r) => r.data),
  adminSpecialists: () => client.get('/admin/specialists').then((r) => r.data),
  adminStorageStatus: () => client.get('/admin/storage-status').then((r) => r.data),

  // --- Tax Specialists (admin CRUD) ---
  adminListSpecialists: () => client.get('/admin/tax-specialists').then((r) => r.data),
  adminCreateSpecialist: (payload) => client.post('/admin/tax-specialists', payload).then((r) => r.data),
  adminGetSpecialist: (id) => client.get(`/admin/tax-specialists/${id}`).then((r) => r.data),
  adminUpdateSpecialist: (id, payload) => client.put(`/admin/tax-specialists/${id}`, payload).then((r) => r.data),
  adminResetSpecialistPassword: (id, new_password) => client.put(`/admin/tax-specialists/${id}/password`, { new_password }).then((r) => r.data),
  adminDeactivateSpecialist: (id) => client.delete(`/admin/tax-specialists/${id}`).then((r) => r.data),
  adminUploadSpecialistPicture: (id, formData) => client.post(`/admin/tax-specialists/${id}/picture`, formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),

  // --- Specialist self-service ---
  myLanguages: () => client.get('/languages').then((r) => r.data),
  mySpecialistProfile: () => client.get('/specialist/profile').then((r) => r.data),
  updateMySpecialistProfile: (payload) => client.put('/specialist/profile', payload).then((r) => r.data),
  uploadMyPicture: (formData) => client.post('/specialist/profile/picture', formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data),
  changeMyPassword: (payload) => client.put('/specialist/password', payload).then((r) => r.data),

  // --- Dropbox integration (admin) ---
  dropboxAuthUrl: () => client.get('/admin/dropbox/auth-url').then((r) => r.data),
  dropboxStatus: () => client.get('/admin/dropbox/status').then((r) => r.data),
  dropboxDisconnect: () => client.post('/admin/dropbox/disconnect').then((r) => r.data),
  dropboxSetActive: (provider) => client.put('/admin/dropbox/active-provider', { provider }).then((r) => r.data),
  dropboxLogs: (limit = 100) => client.get('/admin/dropbox/logs', { params: { limit } }).then((r) => r.data),
  dropboxVerify: () => client.post('/admin/dropbox/verify').then((r) => r.data),

  // --- Emails (admin) ---
  adminEmailTemplates: () => client.get('/admin/emails/templates').then((r) => r.data),
  adminEmailPreview: (payload) => client.post('/admin/emails/preview', payload).then((r) => r.data),
  adminEmailTest: (payload) => client.post('/admin/emails/test', payload).then((r) => r.data),
  adminEmailLogs: (params = {}) => client.get('/admin/emails/logs', { params }).then((r) => r.data),

  // --- Auth (password reset) ---
  forgotPassword: (email, turnstile_token = '') => client.post('/auth/forgot-password', { email, turnstile_token }).then((r) => r.data),
  resetPassword: (payload) => client.post('/auth/reset-password', payload).then((r) => r.data),

  // --- Member email preferences ---
  getEmailPreferences: () => client.get('/me/email-preferences').then((r) => r.data),
  updateEmailPreferences: (payload) => client.put('/me/email-preferences', payload).then((r) => r.data),

  // --- Public contact form ---
  submitContactForm: (payload) => client.post('/public/contact-form', payload).then((r) => r.data),

  // --- Admin leads inbox ---
  adminLeads: (params = {}) => client.get('/admin/leads', { params }).then((r) => r.data),
  adminLeadCounts: () => client.get('/admin/leads/counts').then((r) => r.data),
  adminLeadStatus: (id, status) => client.put(`/admin/leads/${id}/status`, { status }).then((r) => r.data),
  adminLeadDelete: (id) => client.delete(`/admin/leads/${id}`).then((r) => r.data),

  // --- Member-specialist selection ---
  bookingSpecialists: (params = {}) => client.get('/booking/specialists', { params }).then((r) => r.data),
  myBookingSpecialists: (params = {}) => client.get('/me/booking/specialists', { params }).then((r) => r.data),
  myAssignedSpecialist: () => client.get('/me/my-specialist').then((r) => r.data),
  getSpecialistPreference: () => client.get('/me/specialist-preference').then((r) => r.data),
  updateSpecialistPreference: (payload) => client.put('/me/specialist-preference', payload).then((r) => r.data),
  requestSpecialistChange: (payload) => client.post('/me/specialist-change-request', payload).then((r) => r.data),
  adminListChangeRequests: (status) => client.get('/admin/specialist-change-requests', { params: status ? { status } : {} }).then((r) => r.data),
  adminDecideChangeRequest: (id, decision, admin_note = '') => client.put(`/admin/specialist-change-requests/${id}`, { decision, admin_note }).then((r) => r.data),
  adminSetSpecialistSelfServe: (memberId, allow) => client.put(`/admin/members/${memberId}/specialist-self-serve`, { allow }).then((r) => r.data),

  // --- Admin assignment dashboard (Phase 2) ---
  adminSpecialistWorkloads: () => client.get('/admin/specialist-workloads').then((r) => r.data),
  adminGetSpecialistCapacity: (id) => client.get(`/admin/tax-specialists/${id}/capacity`).then((r) => r.data),
  adminSetSpecialistCapacity: (id, payload) => client.put(`/admin/tax-specialists/${id}/capacity`, payload).then((r) => r.data),
  adminUnassignedMembers: () => client.get('/admin/unassigned-members').then((r) => r.data),
  adminBulkReassign: (payload) => client.post('/admin/assignments/bulk-reassign', payload).then((r) => r.data),
  adminAssignSingle: (payload) => client.post('/admin/assignments/assign', payload).then((r) => r.data),

  // --- Invitations + specialist applications ---
  createInvitation: (payload) => client.post('/invitations', payload).then((r) => r.data),
  lookupInvitation: (token) => client.get(`/invitations/lookup/${token}`).then((r) => r.data),
  adminListInvitations: (params = {}) => client.get('/admin/invitations', { params }).then((r) => r.data),
  revokeInvitation: (id) => client.delete(`/invitations/${id}`).then((r) => r.data),
  submitSpecialistApplication: (payload) => client.post('/public/specialist-applications', payload).then((r) => r.data),
  adminListSpecialistApplications: (status) => client.get('/admin/specialist-applications', { params: status ? { status } : {} }).then((r) => r.data),
  adminDecideSpecialistApplication: (id, decision, admin_note = '') => client.put(`/admin/specialist-applications/${id}`, { decision, admin_note }).then((r) => r.data),

  // --- Phase 3 — public specialist directory + dashboard widget ---
  publicListSpecialists: (language) => client.get('/public/specialists', { params: language ? { language } : {} }).then((r) => r.data),
  publicGetSpecialist: (slug) => client.get(`/public/specialists/${slug}`).then((r) => r.data),
  specialistDashboardStats: () => client.get('/specialist/dashboard-stats').then((r) => r.data),
  specialistMySlug: () => client.get('/specialist/my-slug').then((r) => r.data),

  // --- Member <-> Specialist two-way relationship ---
  memberAddSpecialist: (specialist_user_id) => client.post('/me/specialist', { specialist_user_id }).then((r) => r.data),
  memberRequestRemoveSpecialist: (reason) => client.delete('/me/specialist', { data: { reason } }).then((r) => r.data),
  myOffers: (status) => client.get('/me/specialist-offers', { params: status ? { status } : {} }).then((r) => r.data),
  decideOffer: (id, decision, note = '') => client.put(`/me/specialist-offers/${id}`, { decision, note }).then((r) => r.data),
  specialistOfferMember: (member_user_id, message) => client.post('/specialist/member-requests', { member_user_id, message }).then((r) => r.data),
  specialistListOffers: (status) => client.get('/specialist/member-requests', { params: status ? { status } : {} }).then((r) => r.data),
  specialistWithdrawOffer: (id) => client.delete(`/specialist/member-requests/${id}`).then((r) => r.data),
  specialistEligibleMembers: (q) => client.get('/specialist/eligible-members', { params: q ? { q } : {} }).then((r) => r.data),

  // --- PDF viewer (in-app) ---
  myDocumentViewLink: (id) => client.get(`/me/documents/${id}/view-link`).then((r) => r.data),
  specialistDocumentViewLink: (id) => client.get(`/specialist/documents/${id}/view-link`).then((r) => r.data),
  auditPrint: (id) => client.post(`/documents/${id}/audit/print`).then((r) => r.data),

  // --- Password reset flow (MFA-protected) ---
  passwordPolicy: () => client.get('/auth/password/policy').then((r) => r.data),
  passwordRequestCode: (email, turnstile_token = '') => client.post('/auth/password/request-code', { email, turnstile_token }).then((r) => r.data),
  passwordVerifyCode: (email, code) => client.post('/auth/password/verify-code', { email, code }).then((r) => r.data),
  passwordReset: (payload) => client.post('/auth/password/reset', payload).then((r) => r.data),
  passwordRevokeSessions: () => client.post('/auth/password/revoke-sessions').then((r) => r.data),

  // --- Turnstile admin ---
  turnstileSettings: () => client.get('/admin/turnstile/settings').then((r) => r.data),
  turnstileSettingsUpdate: (payload) => client.put('/admin/turnstile/settings', payload).then((r) => r.data),
  turnstileFailures: (params = {}) => client.get('/admin/turnstile/failures', { params }).then((r) => r.data),

  // --- Tax filing workflow / progress tracker ---
  workflowStages: () => client.get('/workflow/stages').then((r) => r.data),
  myTaxFilingYears: () => client.get('/me/tax-filing/years').then((r) => r.data),
  myTaxFilingTimeline: (taxYear) => client.get('/me/tax-filing/timeline', { params: taxYear ? { tax_year: taxYear } : {} }).then((r) => r.data),
  specialistMemberTimeline: (memberId, taxYear) => client.get(`/specialist/members/${memberId}/timeline`, { params: taxYear ? { tax_year: taxYear } : {} }).then((r) => r.data),
  specialistSetMemberStage: (memberId, payload) => client.put(`/specialist/members/${memberId}/timeline/stage`, payload).then((r) => r.data),
  specialistAddMemberNote: (memberId, payload) => client.post(`/specialist/members/${memberId}/timeline/notes`, payload).then((r) => r.data),
  adminListWorkflowStages: () => client.get('/admin/workflow-stages').then((r) => r.data),
  adminCreateWorkflowStage: (payload) => client.post('/admin/workflow-stages', payload).then((r) => r.data),
  adminUpdateWorkflowStage: (id, payload) => client.put(`/admin/workflow-stages/${id}`, payload).then((r) => r.data),
  adminDeleteWorkflowStage: (id) => client.delete(`/admin/workflow-stages/${id}`).then((r) => r.data),
  adminReorderWorkflowStages: (order) => client.post('/admin/workflow-stages/reorder', { order }).then((r) => r.data),
};
